import React, { Suspense, useEffect, useState } from "react";
import { IconButton, MenuItem } from "@mui/material";
import { ID } from "../../helpers/types";
import * as yup from "yup";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { useTranslation } from "react-i18next";
import { Close, ViewComfy } from "@mui/icons-material";
import { IconAdornment } from "../IconAdornment";
import { ConfigurationPickerQuery } from "./__generated__/ConfigurationPickerQuery.graphql";
import { ConfigurationPicker_ScreenConfigs } from "./__generated__/ConfigurationPicker_ScreenConfigs.graphql";
import {
  ConfigScreen,
  ConfigurationPicker_ScreenConfigs_Organisation$key,
} from "./__generated__/ConfigurationPicker_ScreenConfigs_Organisation.graphql";
import { Column } from "../Flex";
import { useDebounced } from "../../hooks/useDebounced";
import { AppText, HelperText } from "../Typography";
import { isEmpty } from "lodash";
import { useReadableScreenTemplateName } from "../../hooks/useReadableTemplateName";
import { LoadingSpinner } from "../LoadingSpinner";
import { Spacer } from "../Spacer";
import { InfiniteScrollMenuItem } from "../InfiniteScroll";
import { useFetchKey } from "../../providers/RelayProvider";
import { Tooltip } from "../Tooltip";
import { TextInput } from "../TextInput";
import { InputDropdown } from "../InputDropdown";

const pageSize = 20;

export type ConfigurationOption = {
  id: ID;
  name: string;
  configType: ConfigScreen;
};

export const configurationPickerValidator: yup.SchemaOf<ConfigurationOption> =
  yup.object({
    id: yup.string().required(),
    name: yup.string().required(),
    configType: yup.mixed<ConfigScreen>().required(),
  });

type ConfigurationPickerProps = {
  value: ConfigurationOption | null;
  onChange: (value: ConfigurationOption | null) => void;
  error?: boolean;
};

export const ConfigurationPicker = (props: ConfigurationPickerProps) => {
  const { t } = useTranslation("ConfigurationPicker");
  const [searchText, setSearchText] = useState(props.value?.name ?? "");
  const debouncedSearchText = useDebounced(searchText, 300);
  const [focused, setFocused] = useState(false);

  const handleClear = () => {
    props.onChange(null);
    setSearchText("");
  };

  useEffect(() => {
    if (!focused) return;

    const handleClick = () => setFocused(false);
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [focused]);

  return (
    <Column
      style={{ position: "relative" }}
      onClick={(ev) => ev.stopPropagation()}
    >
      <TextInput
        label={t("Configuration")}
        maxLength={100}
        onFocus={() => setFocused(true)}
        value={props.value == null ? searchText : props.value.name}
        onChange={(ev) => {
          props.onChange(null);
          setSearchText(ev.target.value);
        }}
        error={props.error}
        InputProps={{
          onKeyDown: (ev) => {
            if (props.value == null || ev.key === "Escape") return;
            if (ev.key === "Backspace") handleClear();
            ev.preventDefault();
            ev.stopPropagation();
          },
          startAdornment: (
            <IconAdornment>
              <ViewComfy />
            </IconAdornment>
          ),
          endAdornment:
            props.value == null && searchText.length === 0 ? undefined : (
              <Tooltip placement="left" title={t("Clear configuration")}>
                <IconButton onClick={() => handleClear()}>
                  <Close />
                </IconButton>
              </Tooltip>
            ),
        }}
        placeholder={t("No configuration selected")}
      />

      {props.value == null && (focused || debouncedSearchText.length > 0) && (
        <InputDropdown>
          <Suspense
            fallback={
              <Column paddingVertical="smallest">
                <MenuItem>
                  <LoadingSpinner />
                  <Spacer size="smallest" />
                  <HelperText>{t("Searching configurations")}</HelperText>
                </MenuItem>
              </Column>
            }
          >
            <ScreenConfigList
              searchText={debouncedSearchText}
              onChange={(opt) => props.onChange(opt)}
            />
          </Suspense>
        </InputDropdown>
      )}
    </Column>
  );
};

const ScreenConfigList = (props: {
  searchText: string;
  onChange: (value: ConfigurationOption | null) => void;
}) => {
  const { t } = useTranslation("ConfigurationPicker");
  const readableName = useReadableScreenTemplateName();
  const { currentOrganisation } = useLazyLoadQuery<ConfigurationPickerQuery>(
    graphql`
      query ConfigurationPickerQuery(
        $after: String
        $first: Int!
        $searchText: String
      ) {
        currentOrganisation {
          id
          ...ConfigurationPicker_ScreenConfigs_Organisation
        }
      }
    `,
    {
      after: null,
      first: pageSize,
      searchText: isEmpty(props.searchText) ? null : props.searchText,
    },
    { fetchKey: useFetchKey("configs") },
  );

  const {
    data: { screenConfigs },
    hasNext,
    loadNext,
    isLoadingNext,
  } = usePaginationFragment<
    ConfigurationPicker_ScreenConfigs,
    ConfigurationPicker_ScreenConfigs_Organisation$key
  >(
    graphql`
      fragment ConfigurationPicker_ScreenConfigs_Organisation on Organisation
      @refetchable(queryName: "ConfigurationPicker_ScreenConfigs") {
        screenConfigs(first: $first, after: $after, searchText: $searchText)
          @connection(key: "ConfigurationPicker_screenConfigs") {
          edges {
            cursor
            node {
              id
              name
              configType
            }
          }
        }
      }
    `,
    currentOrganisation,
  );

  if (screenConfigs.edges.length === 0) {
    return (
      <Column paddingVertical="smallest">
        <MenuItem>
          <HelperText>
            {props.searchText.trim().length > 0
              ? t("No configurations found for '{{searchText}}'", {
                  searchText: props.searchText,
                })
              : t("No configurations found")}
          </HelperText>
        </MenuItem>
      </Column>
    );
  }

  return (
    <Column paddingVertical="smallest">
      {screenConfigs.edges.map(({ node: opt }) => (
        <MenuItem
          key={opt.id}
          onClick={() => props.onChange(opt)}
          value={opt.id}
        >
          <Column>
            <AppText>{opt.name}</AppText>
            <HelperText>{readableName(opt.configType)}</HelperText>
          </Column>
        </MenuItem>
      ))}
      <InfiniteScrollMenuItem
        hasNext={hasNext}
        loadNext={() => loadNext(pageSize)}
        isLoadingNext={isLoadingNext}
      />
    </Column>
  );
};
